// hooks/useEnergyConsumptionExcelDownloader.js

// react
import { useDispatch, useSelector } from 'react-redux';

// api
import { useLazyGetEnergyConsumptionQuery } from '@/api/energy-analysis/calculateEnergySavingsApiSlice';

// components
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';

import * as XLSX from 'xlsx';

/**
 * Usage:
 * const { downloadEnergyExcel, isDownloading } = useEnergyConsumptionExcelDownloader();
 * <Button onClick={() => downloadEnergyExcel(node)}>Download Excel</Button>
 *
 * Optional:
 * downloadEnergyExcel(node, { aggregate: 'day', start, end, includeMetadata: true })
 */
export function useEnergyConsumptionExcelDownloader() {
  const dispatch = useDispatch();
  const selectedAccount = useSelector(getSelectedAccount);
  const timeRange = useSelector(selectTimeRange);
  const [getEnergyConsumptionData, { isFetching: isDownloading }] =
    useLazyGetEnergyConsumptionQuery();

  const downloadEnergyExcel = async (
    node,
    {
      start = timeRange?.start,
      end = timeRange?.end,
      aggregate = 'hour',
      includeMetadata = true,
      sheetName = 'Energy Consumption',
    } = {},
  ) => {
    if (!node?.energyTreeNodeId) {
      console.error('Missing node Id');
      return;
    }
    if (!selectedAccount?.AccountId) {
      console.error('Missing account Id');
      return;
    }
    if (!start || !end) {
      console.error('Missing time range');
      return;
    }

    const result = await getEnergyConsumptionData({
      accountId: selectedAccount.AccountId,
      energyGroupId: node.energyGroupId,
      nodeId: node.energyTreeNodeId,
      start,
      end,
      aggregate,
      filters: [],
    });

    if ('error' in result) {
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
      return;
    }

    const response = result.data;
    const energyData = response?.EnergyData || [];

    if (!energyData.length) {
      dispatch(
        showSnackbar({
          message: 'Ingen data i perioden.',
          severity: 'info',
        }),
      );
      return;
    }

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();

    // Prepare data rows for Excel
    const dataRows = energyData.map((item) => ({
      // '#': index + 1,
      Tidsstempel: item.timestamp,
      'Forbrug (kWh)': item.sum,
      Aggregering: aggregate,
    }));

    // Create main data worksheet
    const worksheet = XLSX.utils.json_to_sheet(dataRows);

    // Set column widths for better readability
    worksheet['!cols'] = [
      // { wch: 8 }, // # column
      { wch: 20 }, // Timestamp column
      { wch: 15 }, // Value column
      { wch: 12 }, // Aggregate column
    ];

    // Add the main data sheet
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Add metadata sheet if requested
    if (includeMetadata) {
      const metadataRows = [
        { Parameter: 'Node Navn', Værdi: node.name || 'N/A' },
        { Parameter: 'Node ID', Værdi: node.energyTreeNodeId },
        { Parameter: 'Energy Group ID', Værdi: node.energyGroupId },
        { Parameter: 'Account ID', Værdi: selectedAccount.AccountId },
        { Parameter: 'Start Dato', Værdi: start },
        { Parameter: 'Slut Dato', Værdi: end },
        { Parameter: 'Aggregering', Værdi: aggregate },
        { Parameter: 'Antal Datapunkter', Værdi: energyData.length },
        {
          Parameter: 'Total Forbrug',
          Værdi:
            energyData
              .reduce((sum, item) => sum + (item.sum || 0), 0)
              .toFixed(2) + ' kWh',
        },
        { Parameter: 'Genereret', Værdi: new Date().toLocaleString('da-DK') },
      ];

      const metadataSheet = XLSX.utils.json_to_sheet(metadataRows);
      metadataSheet['!cols'] = [{ wch: 20 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Metadata');
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `${node.name || 'energy-consumption'}_${timestamp}.xlsx`;

    // Download the Excel file
    XLSX.writeFile(workbook, filename);

    dispatch(
      showSnackbar({
        message: `Excel fil downloadet: ${filename}`,
        severity: 'success',
      }),
    );
  };

  return { downloadEnergyExcel, isDownloading };
}
